import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useFontStyle } from "~/hook/useFontStyle";
import { cn } from "~/lib/utils";

interface TabDef {
  key: string;
  label: string;
}

interface UnderlineTabComponentProps {
  tabs: TabDef[];
  selectedTab: string;
  onTabChange: (tab: string) => void;
  className?: string;
  containerClassName?: string;
  fullWidth?: boolean;
}

export const UnderlineTabComponent = ({ tabs, selectedTab, onTabChange, className, containerClassName, fullWidth }: UnderlineTabComponentProps) => {
  const { medium } = useFontStyle();
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [underline, setUnderline] = useState({ left: 0, width: 0 });

  useEffect(() => {
    const idx = tabs.findIndex((t) => t.key === selectedTab);
    const el = tabRefs.current[idx];
    if (el) {
      setUnderline({ left: el.offsetLeft, width: el.offsetWidth });
    }
  }, [selectedTab, tabs]);

  return (
    <div className={cn("relative w-full", containerClassName)}>
      {/* Tab row */}
      <div className={cn("relative flex gap-x-5", fullWidth ? "w-full" : "w-fit")}>
        {tabs.map((tab, idx) => {
          const isActive = tab.key === selectedTab;
          return (
            <button
              key={tab.key}
              ref={(el) => {
                tabRefs.current[idx] = el;
              }}
              onClick={() => onTabChange(tab.key)}
              className={cn(
                "relative pb-2.5 md:pb-3 body_text_m transition-colors duration-200 cursor-pointer whitespace-nowrap",
                fullWidth ? "flex-1 text-center" : "",
                isActive ? "text-textHeadingColor" : "text-textColor/50 hover:text-textColor/70",
                medium,
                className,
              )}
            >
              {tab.label}
            </button>
          );
        })}

        {/* Sliding underline */}
        <motion.span
          animate={{ left: underline.left, width: underline.width }}
          transition={{ type: "spring", stiffness: 400, damping: 35 }}
          className="absolute bottom-0 h-[2.5px] md:h-[3px] rounded-full bg-buttonColor"
          style={{ left: underline.left, width: underline.width }}
        />
      </div>

      {/* Full-width separator */}
      <div className="w-full h-px bg-textHeadingColor/15" />
    </div>
  );
};

export default UnderlineTabComponent;
